/**
 * Phase 105-Agent-A — TaskDetailSheet · with Phase-B stage advancer.
 *
 * Right-side panel (desktop) / bottom-sheet (mobile) opened when the
 * agent taps a pin. Drives the full lifecycle:
 *
 *   1. assigned / pickup_assigned → Reject · Accept (Phase A)
 *   2. accepted (not yet verified) → inline MultiParcelChecklist
 *   3. accepted + verified         → CTA "Capture pickup proof"
 *      → opens ProofCapture(kind="pickup")
 *   4. picked_up / in_transit /
 *      out_for_delivery / linehaul / at_hub → CTA "Capture delivery proof"
 *      → opens ProofCapture(kind="delivery")
 *   5. delivered + COD pending     → CTA "Mark COD collected"
 *      → opens CodCollectModal (auto-opens once after delivery proof too)
 *   6. delivered (not COD or COD collected) → static "Done" badge
 *
 * The same 5-block info structure (Customer · Pickup · Drop · Parcel ·
 * Financial) remains in the body. Maps deep-link buttons (Pickup /
 * Drop) sit just below using the shared utils/mapsDeepLink helper.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, MapPin, Package, User, Phone, Navigation, CheckCircle2, Ban, IndianRupee, ExternalLink, Clock, Camera, Banknote, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../services/api";
import { gmapsForTask } from "../../utils/mapsDeepLink";
import MultiParcelChecklist from "./MultiParcelChecklist";
import ProofCapture from "./ProofCapture";
import CodCollectModal from "./CodCollectModal";

function maskPhone(p) {
  if (!p) return "";
  const s = String(p);
  if (s.length < 6) return s;
  return s.slice(0, 2) + "•".repeat(Math.max(2, s.length - 4)) + s.slice(-2);
}

function fmt(ts) {
  if (!ts) return null;
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return null; }
}

function totalAmount(o) {
  const p = (o?.price && typeof o.price === "object") ? o.price : {};
  return p.total ?? p.final_price ?? o?.total_amount ?? null;
}

function isCod(o) { return String(o?.payment_method || "").toLowerCase() === "cod"; }

// Stage classifier — drives the footer CTA + inline panels.
const PICKUP_PENDING = new Set(["assigned", "pickup_assigned", "pending"]);
const DELIVERY_PENDING = new Set([
  "picked_up", "picked", "in_transit", "at_hub", "at_origin_hub",
  "at_dest_hub", "out_for_delivery", "linehaul",
]);

function classifyStage(task) {
  if (!task) return "unknown";
  const s = String(task.status || "").toLowerCase();
  const verified = !!task.parcel_verifications?.all_verified
    || (Array.isArray(task.items) && task.items.length <= 1 && s === "accepted");
  if (PICKUP_PENDING.has(s))                           return "pending_accept";
  if (s === "accepted" && !verified)                   return "verify_parcels";
  if (s === "accepted" && verified)                    return "capture_pickup";
  if (DELIVERY_PENDING.has(s))                         return "capture_delivery";
  if (s === "delivered" && isCod(task) && !task.cod_collected_at) return "collect_cod";
  if (s === "delivered")                               return "done";
  if (["cancelled", "failed", "refunded"].includes(s)) return "terminal";
  return "unknown";
}

function ExpireCountdown({ assignedAt, expireSeconds = 60, onExpire }) {
  const expiresAt = useMemo(() => {
    if (!assignedAt) return null;
    try { return new Date(assignedAt).getTime() + expireSeconds * 1000; } catch { return null; }
  }, [assignedAt, expireSeconds]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  const remaining = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : null;
  useEffect(() => {
    if (remaining === 0 && onExpire) {
      const t = setTimeout(onExpire, 100);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining === 0]);

  if (!expiresAt) return null;
  const pct = Math.max(0, Math.min(100, (remaining / expireSeconds) * 100));
  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800" data-testid="auto-expire-countdown">
      <Clock className="h-3.5 w-3.5" />
      <span className="text-[11px] font-bold tabular-nums">
        Accept within {remaining}s
      </span>
      <div className="w-14 h-1.5 rounded-full bg-amber-200 overflow-hidden">
        <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function TaskDetailSheet({ task, onClose, onAccept, onRejectClick, onRefresh, onTaskUpdated }) {
  const [acting, setActing] = useState(false);
  const [proofKind, setProofKind] = useState(null);   // null | "pickup" | "delivery"
  const [codOpen, setCodOpen] = useState(false);

  if (!task) return null;

  const total = totalAmount(task);
  const cod = isCod(task);
  const gmapsPickupUrl = gmapsForTask(task, "pickup");
  const gmapsDeliveryUrl = gmapsForTask(task, "delivery");
  const photos = (task.items || []).map((it, i) => ({
    src: it.package_photo_url || it.customer_package_photo,
    name: it.item_name || `Parcel ${i + 1}`,
  })).filter((p) => !!p.src);
  if (photos.length === 0 && task.package_photo_url) {
    photos.push({ src: task.package_photo_url, name: task.package_description || "Package" });
  }

  const stage = classifyStage(task);
  const reload = () => { if (onTaskUpdated) onTaskUpdated(); else if (onRefresh) onRefresh(); };

  const accept = async () => {
    setActing(true);
    try {
      await api.post(`/orders/accept/${task.tracking_id}`);
      toast.success(`${task.tracking_id} accepted.`);
      if (onAccept) onAccept(task.tracking_id);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't accept the task";
      toast.error(typeof detail === "string" ? detail : "Couldn't accept the task");
    } finally {
      setActing(false);
    }
  };

  // Stage CTA — main button rendered in the footer when not in
  // pending_accept (which has its own Reject + Accept layout).
  const stageCta = (() => {
    switch (stage) {
      case "capture_pickup":
        return {
          label: "Capture pickup proof",
          tone: "bg-emerald-600 hover:bg-emerald-700",
          icon: <Camera className="h-4 w-4 mr-1.5" />,
          onClick: () => setProofKind("pickup"),
          testid: "stage-cta-capture-pickup",
        };
      case "capture_delivery":
        return {
          label: "Capture delivery proof",
          tone: "bg-blue-600 hover:bg-blue-700",
          icon: <Camera className="h-4 w-4 mr-1.5" />,
          onClick: () => setProofKind("delivery"),
          testid: "stage-cta-capture-delivery",
        };
      case "collect_cod":
        return {
          label: `Mark COD collected · ₹${total ?? 0}`,
          tone: "bg-amber-500 hover:bg-amber-600",
          icon: <Banknote className="h-4 w-4 mr-1.5" />,
          onClick: () => setCodOpen(true),
          testid: "stage-cta-collect-cod",
        };
      case "done":
        return {
          label: "Done · Order complete",
          tone: "bg-slate-200 text-slate-600 cursor-default",
          icon: <ShieldCheck className="h-4 w-4 mr-1.5" />,
          onClick: null,
          testid: "stage-cta-done",
          disabled: true,
        };
      case "terminal":
        return {
          label: `Terminal · ${task.status}`,
          tone: "bg-rose-100 text-rose-700 cursor-default",
          icon: <Ban className="h-4 w-4 mr-1.5" />,
          onClick: null,
          testid: "stage-cta-terminal",
          disabled: true,
        };
      default:
        return null;
    }
  })();

  return (
    <div
      className="absolute inset-0 sm:relative sm:inset-auto sm:w-[420px] sm:max-w-[460px] sm:h-full bg-white sm:border-l sm:border-slate-200 shadow-xl z-[1100] flex flex-col"
      data-testid="task-detail-sheet"
      data-stage={stage}
    >
      {/* Header */}
      <div className="flex-shrink-0 px-5 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold text-slate-500">{task.tracking_id}</p>
          <h2 className="font-['Outfit'] text-lg font-bold text-slate-900 mt-0.5 capitalize">
            {String(task.status || "").replace(/_/g, " ")}
          </h2>
          {task.created_at && <p className="text-[11px] text-slate-400 mt-0.5">Placed {fmt(task.created_at)}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
          data-testid="task-sheet-close"
        >
          <X className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {stage === "pending_accept" && task.assigned_at && (
          <ExpireCountdown
            assignedAt={task.assigned_at}
            expireSeconds={60}
            onExpire={onRefresh}
          />
        )}

        {/* Customer */}
        <section data-testid="task-sheet-customer">
          <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1.5">
            <User className="h-3 w-3" /> Customer
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1.5">{task.delivery_contact_name || "—"}</p>
          {task.delivery_phone && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
              <Phone className="h-3 w-3" /> {maskPhone(task.delivery_phone)}
            </p>
          )}
        </section>

        {/* Pickup */}
        <section data-testid="task-sheet-pickup">
          <p className="text-[10px] uppercase font-bold tracking-wider text-emerald-600 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> Pickup
          </p>
          <p className="text-sm text-slate-900 mt-1.5 leading-relaxed">{task.pickup_address || "—"}</p>
          {task.pickup_contact_name && (
            <p className="text-xs text-slate-500 mt-1">{task.pickup_contact_name}</p>
          )}
        </section>

        {/* Drop */}
        <section data-testid="task-sheet-drop">
          <p className="text-[10px] uppercase font-bold tracking-wider text-orange-600 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> Drop
          </p>
          <p className="text-sm text-slate-900 mt-1.5 leading-relaxed">{task.delivery_address || "—"}</p>
        </section>

        {/* Parcel */}
        <section data-testid="task-sheet-parcel">
          <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1.5">
            <Package className="h-3 w-3" /> Parcel
          </p>
          <p className="text-sm text-slate-800 mt-1.5">{task.package_description || "Package"}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
            {task.package_weight && <span>{task.package_weight} kg</span>}
            {(task.length_cm || task.width_cm || task.height_cm) && (
              <span>{task.length_cm || "?"} × {task.width_cm || "?"} × {task.height_cm || "?"} cm</span>
            )}
            {Array.isArray(task.items) && task.items.length > 1 && (
              <span className="font-semibold text-slate-700">{task.items.length} parcels</span>
            )}
          </div>
          {photos.length > 0 && (
            <div className="flex gap-2 mt-2 overflow-x-auto" data-testid="task-sheet-photo-strip">
              {photos.slice(0, 4).map((p, i) => (
                <img
                  key={i}
                  src={p.src}
                  alt={p.name}
                  className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0"
                />
              ))}
            </div>
          )}
        </section>

        {/* Financial */}
        <section className="flex items-center justify-between bg-slate-50 rounded-xl p-3" data-testid="task-sheet-financial">
          <div>
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Order value</p>
            <p className="font-['Outfit'] text-lg font-bold text-slate-900 mt-0.5 inline-flex items-center">
              <IndianRupee className="h-4 w-4" />{total ?? "—"}
            </p>
          </div>
          {cod && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
              task.cod_collected_at
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-100 text-amber-800 border-amber-200"
            }`} data-testid="task-sheet-cod-badge">
              {task.cod_collected_at ? "COD collected" : "COD pending"}
            </span>
          )}
        </section>

        {/* Google Maps deep-links — pickup first (most useful pre-arrival), then drop. */}
        {(gmapsPickupUrl || gmapsDeliveryUrl) && (
          <div className="grid grid-cols-1 gap-2" data-testid="task-sheet-gmaps-block">
            {gmapsPickupUrl && (
              <a
                href={gmapsPickupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold border border-emerald-200 transition"
                data-testid="task-sheet-pickup-in-maps"
              >
                <MapPin className="h-4 w-4" /> Pickup in Maps
                <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            )}
            {gmapsDeliveryUrl && (
              <a
                href={gmapsDeliveryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold transition"
                data-testid="task-sheet-open-in-maps"
              >
                <Navigation className="h-4 w-4" /> Drop in Maps
                <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            )}
          </div>
        )}

        {/* Stage panel — inline MultiParcelChecklist when verify is the next step. */}
        {stage === "verify_parcels" && (
          <div data-testid="stage-panel-verify">
            <MultiParcelChecklist
              order={task}
              onVerified={() => {
                toast.success("Parcels verified — capture the pickup proof next.");
                reload();
              }}
            />
          </div>
        )}
      </div>

      {/* Sticky footer — stage-aware CTA */}
      <div className="flex-shrink-0 px-5 py-3 border-t border-slate-100 bg-white space-y-2" data-testid="task-sheet-action-bar">
        {stage === "pending_accept" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onRejectClick && onRejectClick(task)}
              disabled={acting}
              className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
              data-testid="task-sheet-reject-btn"
            >
              <Ban className="h-4 w-4 mr-1.5" /> Reject
            </Button>
            <Button
              onClick={accept}
              disabled={acting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
              data-testid="task-sheet-accept-btn"
            >
              {acting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Accepting…</>
                      : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Accept</>}
            </Button>
          </div>
        ) : stage === "verify_parcels" ? (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="stage-blocked-reason">
            <ArrowRight className="h-3.5 w-3.5" /> Almost there — verify each parcel above to unlock pickup proof.
          </p>
        ) : stageCta ? (
          <button
            type="button"
            onClick={stageCta.onClick || undefined}
            disabled={!!stageCta.disabled}
            className={`w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-bold text-white transition ${stageCta.tone}`}
            data-testid={stageCta.testid}
            aria-label={stageCta.label}
          >
            {stageCta.icon}{stageCta.label}
          </button>
        ) : null}
      </div>

      {/* Proof capture dialog (pickup or delivery) */}
      <ProofCapture
        open={!!proofKind}
        onOpenChange={(v) => { if (!v) setProofKind(null); }}
        order={task}
        kind={proofKind || "pickup"}
        onSuccess={(data) => {
          reload();
          // If this was a delivery completion on a COD order that hasn't
          // been collected yet, auto-open the COD modal so the agent
          // can't forget the cash.
          if (proofKind === "delivery" && data?.status === "delivered" && cod && !task.cod_collected_at) {
            setTimeout(() => setCodOpen(true), 250);
          }
        }}
      />

      {/* COD collect dialog */}
      <CodCollectModal
        open={codOpen}
        onOpenChange={setCodOpen}
        order={task}
        onCollected={() => reload()}
      />
    </div>
  );
}
